import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Job, JobLogLine, StepStatus } from '@api/api';
import { endpoints, qk } from './endpoints';
import { openStream } from './sse';

export const isActiveJob = (j: Job | undefined) => !!j && (j.status === 'queued' || j.status === 'running');

/** A job with its full log, followed live over SSE while it runs. */
export function useJobStream(id: number) {
  const qc = useQueryClient();
  const job = useQuery({ queryKey: qk.job(id), queryFn: () => endpoints.job(id), refetchInterval: (q) => (isActiveJob(q.state.data) ? 15_000 : false) });
  const [lines, setLines] = useState<JobLogLine[]>([]);
  const [live, setLive] = useState(false);
  const seen = useRef(new Set<number>());

  const add = (ls: JobLogLine[]) => {
    const fresh = ls.filter((l) => !seen.current.has(l.seq));
    if (!fresh.length) return;
    fresh.forEach((l) => seen.current.add(l.seq));
    setLines((prev) => [...prev, ...fresh].sort((a, b) => a.seq - b.seq));
  };

  useEffect(() => {
    seen.current = new Set();
    setLines([]);
    endpoints.jobLog(id).then((r) => add(r.lines), () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const active = isActiveJob(job.data);
  useEffect(() => {
    if (!active) return;
    const close = openStream(
      endpoints.jobStreamPath(id),
      {
        line: (d) => add([d as JobLogLine]),
        step: (d) => {
          const s = d as { name: string; status: StepStatus };
          qc.setQueryData<Job>(qk.job(id), (j) => (j ? { ...j, status: j.status === 'queued' ? 'running' : j.status, steps: j.steps.map((x) => (x.name === s.name ? { ...x, status: s.status } : x)) } : j));
        },
        done: (d) => {
          const j = d as Job;
          qc.setQueryData(qk.job(id), j);
          setLive(false);
          endpoints.jobLog(id).then((r) => add(r.lines), () => {});
          void qc.invalidateQueries({ queryKey: ['jobs'] });
          void qc.invalidateQueries({ queryKey: ['overview'] });
          void qc.invalidateQueries({ queryKey: ['clones'] });
          void qc.invalidateQueries({ queryKey: ['releases'] });
          if (j.cloneId) void qc.invalidateQueries({ queryKey: ['clone', j.cloneId] });
        },
      },
      { onOpen: () => setLive(true), onError: () => setLive(false) },
    );
    return () => {
      close();
      setLive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, active]);

  return { job, lines, live };
}
